import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue } from 'framer-motion'
import { 
  ArrowLeft, ChevronLeft, ChevronRight, Sparkles, Star, Zap, Shield, Award, 
  ArrowRight, Clock, Users, Target, ExternalLink, Github, Calendar, User,
  Code, Palette, Smartphone, Globe, Rocket, TrendingUp, CheckCircle, Layers
} from 'lucide-react'
import * as LucideIcons from 'lucide-react'
import { projectApi, assetUrl, contactApi } from '../services/api'

const DynamicIcon = ({ name, className }: { name: string | null; className?: string }) => {
  if (!name) return <div className={className} />
  
  if (name.includes('/') || name.includes('.')) {
     return <img src={assetUrl(name)} alt="" className={`object-contain ${className}`} />
  }
  
  // @ts-expect-error LucideIcons is a module
  const Icon = LucideIcons[name]
  if (Icon) return <Icon className={className} />
  
  return <div className={className} />
}

type Project = {
  id: number
  title: string
  category?: string
  description: string
  technologies: (string | { name: string; icon_type?: 'library' | 'upload' | 'none'; icon_value?: string })[]
  image?: string | null
  image_url?: string | null
  button_text?: string
  live_demo_url?: string | null
  github_url?: string | null
  gallery?: string[]
  testimonials?: { author?: string; text: string }[]
  case_study_content?: string
  slug: string
  views_count?: number
  client?: string
  date?: string
  specs_heading?: string
  specs_content?: string
  tech_heading?: string
  tech_heading_icon_type?: 'library' | 'upload' | 'none';
  tech_heading_icon_value?: string;
}

const ProjectDetail: React.FC = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [project, setProject] = useState<Project | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [index, setIndex] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', message: '' })

  const { scrollYProgress } = useScroll()
  const yHero = useTransform(scrollYProgress, [0, 1], [0, -120])
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 15])

  useEffect(() => {
    const toSlug = (title: string, id?: number | string) => {
      const base = String(title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'project'
      return id != null ? `${base}-${id}` : base
    }
    const fetch = async () => {
      let p: Project | null = null
      const currentSlug = String(slug || '')

      if (currentSlug) {
        try {
          const bySlug = await projectApi.getBySlug(currentSlug)
          if (bySlug?.success) p = bySlug.data as Project
        } catch { /* continue */ }
      }

      if (!p && currentSlug) {
        try {
          const idMatch = currentSlug.match(/-(\d+)$/)
          const maybeId = idMatch ? Number(idMatch[1]) : (/^\d+$/.test(currentSlug) ? Number(currentSlug) : null)
          if (maybeId != null && !Number.isNaN(maybeId)) {
            const byId = await projectApi.getById(maybeId)
            if (byId?.success) p = byId.data as Project
          }
        } catch { /* continue */ }
      }

      if (!p && currentSlug) {
        try {
          const res = await projectApi.getAll()
          if (res?.success && Array.isArray(res.data)) {
            const list = res.data as Project[]
            p = list.find((r) => String(r.slug || toSlug(String(r.title || ''), r.id)) === currentSlug) || null
          }
        } catch { /* continue */ }
      }

      if (!p) {
        setProject(null)
        setNotFound(true)
        return
      }

      setProject(p)
    }
    fetch()
  }, [slug])

  const gallery = useMemo(() => {
    const imgs = project?.gallery || []
    const fallbacks = [project?.image, project?.image_url].filter(Boolean) as string[]
    return imgs.length ? imgs : fallbacks
  }, [project])

  const currentImage = gallery[index] || null

  const onPrev = () => setIndex((i) => (i - 1 + gallery.length) % Math.max(gallery.length, 1))
  const onNext = () => setIndex((i) => (i + 1) % Math.max(gallery.length, 1))

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setSubmitting(true)
      await contactApi.submit({ name: form.name, email: form.email, subject: `Project Inquiry: ${project?.title || 'Project'}`, message: form.message })
      setForm({ name: '', email: '', message: '' })
      setSubmitting(false)
    } catch { setSubmitting(false) }
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-black text-white pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-gray-400">{notFound ? 'Project not found' : 'Loading project...'}</div>
        </div>
      </div>
    )
  }

  const techIcons = [
    { icon: Code, label: 'Development' },
    { icon: Palette, label: 'Design' },
    { icon: Smartphone, label: 'Mobile' },
    { icon: Globe, label: 'Web' },
    { icon: Rocket, label: 'Performance' },
    { icon: TrendingUp, label: 'Growth' }
  ]

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <motion.div 
          style={{ 
            y: yHero as unknown as number, 
            rotate,
          }} 
          className="absolute top-[-20%] left-[-20%] w-[70vw] h-[70vw] bg-gradient-to-br from-orange-600/20 to-orange-400/10 blur-[150px] rounded-full animate-pulse" 
        />
        <motion.div 
          style={{ 
            y: yHero as unknown as number,
            rotate: -rotate,
          }} 
          className="absolute bottom-[-20%] right-[-20%] w-[70vw] h-[70vw] bg-gradient-to-tl from-orange-500/20 to-orange-600/10 blur-[150px] rounded-full animate-pulse" 
        />
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="h-full w-full" style={{
            backgroundImage: `
              linear-gradient(rgba(234, 88, 12, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(234, 88, 12, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            animation: 'grid-move 20s linear infinite'
          }}></div>
        </div>

        {/* Floating Particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-orange-500/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes grid-move {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          {/* Decorative Elements */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-full blur-2xl animate-pulse" />
          <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-tl from-orange-400/20 to-orange-500/10 rounded-full blur-2xl animate-pulse delay-1000" />
          <div className="absolute bottom-20 left-1/4 w-40 h-40 bg-gradient-to-br from-orange-600/15 to-orange-400/5 rounded-full blur-3xl animate-pulse delay-500" />
          
          <div className="text-center mb-16">
            <motion.div 
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-orange-500/20 to-orange-600/10 border border-orange-500/40 text-sm backdrop-blur-xl mb-12 shadow-2xl shadow-orange-500/20"
            >
              <div className="relative">
                <Sparkles size={18} className="text-orange-400" />
                <motion.div 
                  className="absolute inset-0 bg-orange-400 rounded-full blur-md"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>
              <span className="text-orange-300 font-bold tracking-wide">FEATURED PROJECT</span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="text-7xl md:text-9xl font-black italic mb-12 leading-tight tracking-tight"
            >
              <span className="inline-block transform hover:scale-105 transition-transform duration-300">
                {project.title.split(' ')[0]}
              </span>
              <br />
              <span className="inline-block bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent transform hover:scale-105 transition-transform duration-300">
                {project.title.split(' ').slice(1).join(' ')}
              </span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-2xl md:text-3xl text-gray-200 leading-relaxed max-w-4xl mx-auto mb-16 font-light"
            >
              <span className="relative">
                {project.description}
                <motion.div 
                  className="absolute -bottom-2 left-0 h-0.5 bg-gradient-to-r from-orange-500 to-orange-600"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ delay: 1, duration: 1 }}
                />
              </span>
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12"
            >
              {project.live_demo_url && (
                <motion.a 
                  href={project.live_demo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative px-10 py-5 bg-gradient-to-r from-orange-500 to-orange-600 text-black font-black rounded-full hover:from-orange-400 hover:to-orange-500 transition-all duration-500 flex items-center gap-4 shadow-2xl shadow-orange-500/30 hover:shadow-orange-500/50"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex items-center gap-4">
                    <ExternalLink size={24} />
                    <span className="text-lg">LIVE DEMO</span>
                    <ArrowRight className="group-hover:translate-x-2 transition-transform duration-300" size={24} />
                  </div>
                </motion.a>
              )}
              {project.github_url && (
                <motion.a 
                  href={project.github_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group px-10 py-5 bg-transparent border-2 border-white/60 text-white font-bold rounded-full hover:bg-white/10 hover:border-white transition-all duration-500 flex items-center gap-4 backdrop-blur-sm"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Github size={24} />
                  <span className="text-lg">VIEW CODE</span>
                </motion.a>
              )}
            </motion.div>

            {/* Enhanced Project Meta */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="flex flex-wrap justify-center gap-12 text-gray-300"
            >
              {project.category && (
                <motion.div 
                  className="flex items-center gap-3 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10"
                  whileHover={{ scale: 1.05, backgroundColor: 'rgba(234, 88, 12, 0.1)' }}
                >
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                  <Target size={18} className="text-orange-400" />
                  <span className="font-medium">{project.category}</span>
                </motion.div>
              )}
              {project.client && (
                <motion.div 
                  className="flex items-center gap-3 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10"
                  whileHover={{ scale: 1.05, backgroundColor: 'rgba(234, 88, 12, 0.1)' }}
                >
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                  <User size={18} className="text-orange-400" />
                  <span className="font-medium">{project.client}</span>
                </motion.div>
              )}
              {project.date && (
                <motion.div 
                  className="flex items-center gap-3 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10"
                  whileHover={{ scale: 1.05, backgroundColor: 'rgba(234, 88, 12, 0.1)' }}
                >
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                  <Calendar size={18} className="text-orange-400" />
                  <span className="font-medium">{project.date}</span>
                </motion.div>
              )}
            </motion.div>
          </div>

          {/* Hero Image Gallery */}
          <motion.div 
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="relative max-w-6xl mx-auto"
          >
            {/* Glow Effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 via-orange-600/10 to-orange-500/20 rounded-[4rem] blur-3xl animate-pulse" />
            
            <div className="relative aspect-video bg-gradient-to-br from-white/10 to-white/5 border-2 border-white/20 rounded-[3rem] overflow-hidden shadow-2xl shadow-orange-500/20 backdrop-blur-xl">
              <AnimatePresence initial={false} mode="wait">
                {currentImage && (
                  <motion.img
                    key={currentImage}
                    src={assetUrl(currentImage)}
                    className="w-full h-full object-cover"
                    initial={{ opacity: 0, scale: 1.1, rotate: 2 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    exit={{ opacity: 0, scale: 0.9, rotate: -2 }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                  />
                )}
              </AnimatePresence>
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
              
              {/* Enhanced Gallery Controls */}
              {gallery.length > 1 && (
                <div className="absolute bottom-8 right-8 flex gap-4 z-30">
                  <motion.button 
                    onClick={onPrev} 
                    className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-xl border-2 border-white/30 text-white flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 hover:scale-110 active:scale-95 shadow-xl"
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ChevronLeft size={28} />
                  </motion.button>
                  <motion.button 
                    onClick={onNext} 
                    className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-xl border-2 border-white/30 text-white flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 hover:scale-110 active:scale-95 shadow-xl"
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ChevronRight size={28} />
                  </motion.button>
                </div>
              )}

              {/* Enhanced Image Counter */}
              {gallery.length > 1 && (
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 }}
                  className="absolute top-8 right-8 px-6 py-3 bg-black/60 backdrop-blur-xl border-2 border-white/20 rounded-full shadow-xl"
                >
                  <span className="text-sm font-bold text-white">
                    <span className="text-orange-400">{index + 1}</span>
                    <span className="text-gray-400 mx-2">/</span>
                    <span className="text-gray-400">{gallery.length}</span>
                  </span>
                </motion.div>
              )}

              {/* Image Navigation Dots */}
              {gallery.length > 1 && (
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-30">
                  {gallery.map((_, i) => (
                    <motion.button
                      key={i}
                      onClick={() => setIndex(i)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        i === index 
                          ? 'bg-orange-500 w-8' 
                          : 'bg-white/30 hover:bg-white/50'
                      }`}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.8 }}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Technologies Section */}
      {Array.isArray(project.technologies) && project.technologies.length > 0 && (
        <section className="relative py-24 bg-gradient-to-b from-black via-black/95 to-orange-950/20">
          {/* Section Background Effects */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-20"
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-orange-500/20 to-orange-600/10 border border-orange-500/30 text-sm backdrop-blur-xl mb-8"
              >
                <Zap size={18} className="text-orange-400" />
                <span className="text-orange-300 font-bold tracking-wide">TECHNOLOGY STACK</span>
              </motion.div>
              
              <h2 className="text-5xl md:text-7xl font-black mb-8">
                Technologies <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent">Used</span>
              </h2>
              <div className="w-32 h-1 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto mb-8 shadow-lg shadow-orange-500/50"></div>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Cutting-edge technologies powering this amazing project
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {project.technologies.map((tech, i) => (
                <motion.div 
                  key={i} 
                  initial={{ opacity: 0, y: 40, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  className="group relative"
                >
                  {/* Glow Effect */}
                  <div className="absolute -inset-4 bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative p-8 bg-gradient-to-br from-white/10 to-white/5 border-2 border-white/20 rounded-3xl hover:border-orange-500/50 hover:bg-gradient-to-br hover:from-orange-500/10 hover:to-orange-600/5 hover:-translate-y-3 transition-all duration-500 backdrop-blur-xl shadow-xl shadow-orange-500/10">
                    <div className="relative w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-500 border border-orange-500/30">
                      {typeof tech === 'string' ? (
                        techIcons[i % techIcons.length].icon({ className: "w-8 h-8 text-orange-400" })
                      ) : (
                        <DynamicIcon name={tech.icon_value || null} className="w-8 h-8 text-orange-400" />
                      )}
                    </div>
                    <div className="text-center">
                      <h3 className="text-white font-black text-lg mb-2 group-hover:text-orange-300 transition-colors">
                        {typeof tech === 'string' ? tech : tech.name}
                      </h3>
                      <div className="w-0 h-0.5 bg-gradient-to-r from-orange-500 to-orange-600 group-hover:w-full transition-all duration-500 mx-auto" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Case Study/Details */}
      {(project.case_study_content || project.specs_content) && (
        <section className="relative py-20">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-black mb-8">
                Project <span className="text-orange-500">Details</span>
              </h2>
              <div className="w-24 h-1 bg-orange-500 mx-auto mb-8"></div>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {project.case_study_content && (
                <motion.div 
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="prose prose-invert prose-lg max-w-none text-gray-300 text-xl leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: project.case_study_content }} 
                />
              )}
              
              {project.specs_content && (
                <motion.div 
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8"
                >
                  <h3 className="text-2xl font-black text-white mb-6">{project.specs_heading || 'Specifications'}</h3>
                  <div 
                    className="prose prose-invert max-w-none text-gray-300"
                    dangerouslySetInnerHTML={{ __html: project.specs_content }} 
                  />
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {Array.isArray(project.testimonials) && project.testimonials.length > 0 && (
        <section className="relative py-20 bg-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-black mb-8">
                Client <span className="text-orange-500">Testimonials</span>
              </h2>
              <div className="w-24 h-1 bg-orange-500 mx-auto mb-8"></div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {project.testimonials.map((testimonial, i) => (
                <motion.div 
                  key={i} 
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-8 bg-black/30 border border-white/10 rounded-2xl backdrop-blur-sm"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} size={16} className="text-orange-500 fill-orange-500" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                      <User size={20} className="text-orange-500" />
                    </div>
                    <div>
                      <div className="text-white font-black">{testimonial.author}</div>
                      <div className="text-gray-400 text-sm">Client</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {gallery.length > 1 && (
        <section className="relative py-20">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-black mb-8">
                Project <span className="text-orange-500">Gallery</span>
              </h2>
              <div className="w-24 h-1 bg-orange-500 mx-auto mb-8"></div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {gallery.map((img, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="aspect-video bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-orange-500/50 transition-all duration-300 cursor-pointer"
                  onClick={() => setIndex(i)}
                >
                  <img 
                    src={assetUrl(img)} 
                    alt="" 
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" 
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 backdrop-blur-xl border border-orange-500/30 rounded-3xl p-12 text-center"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-8">
              Interested in This <span className="text-orange-500">Project?</span>
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
              Let's discuss how we can create something amazing for your business too.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-orange-500 text-black font-black rounded-full hover:bg-orange-400 transition-all duration-300 flex items-center gap-3 group"
              >
                Get In Touch
                <ArrowRight className="group-hover:translate-x-2 transition-transform" size={20} />
              </button>
              <Link 
                to="/projects"
                className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-full hover:bg-white/10 transition-colors"
              >
                View More Projects
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="relative py-24 bg-gradient-to-b from-black via-black/95 to-orange-950/30">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-tl from-orange-600/20 to-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-orange-500/20 to-orange-600/10 border border-orange-500/30 text-sm backdrop-blur-xl mb-8"
            >
              <Rocket size={18} className="text-orange-400" />
              <span className="text-orange-300 font-bold tracking-wide">START YOUR PROJECT</span>
            </motion.div>
            
            <h2 className="text-5xl md:text-7xl font-black mb-8">
              Start Your <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent">Project</span>
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto mb-8 shadow-lg shadow-orange-500/50"></div>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-12">
              Ready to bring your vision to life? Let's create something amazing together.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Glow Effect */}
            <div className="absolute -inset-8 bg-gradient-to-r from-orange-500/20 via-orange-600/10 to-orange-500/20 rounded-3xl blur-3xl" />
            
            <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-2xl border-2 border-white/20 rounded-3xl p-8 md:p-16 shadow-2xl shadow-orange-500/20">
              <form onSubmit={onSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <label className="text-sm text-orange-400 font-bold tracking-wide uppercase">Your Name</label>
                    <input 
                      value={form.name} 
                      onChange={(e) => setForm({ ...form, name: e.target.value })} 
                      placeholder="John Doe" 
                      className="w-full bg-black/40 border-2 border-white/20 rounded-2xl px-6 py-4 text-white placeholder-gray-500 focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all duration-300 outline-none backdrop-blur-sm"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm text-orange-400 font-bold tracking-wide uppercase">Email Address</label>
                    <input 
                      value={form.email} 
                      onChange={(e) => setForm({ ...form, email: e.target.value })} 
                      placeholder="john@example.com" 
                      className="w-full bg-black/40 border-2 border-white/20 rounded-2xl px-6 py-4 text-white placeholder-gray-500 focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all duration-300 outline-none backdrop-blur-sm"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="text-sm text-orange-400 font-bold tracking-wide uppercase">Project Details</label>
                  <textarea 
                    value={form.message} 
                    onChange={(e) => setForm({ ...form, message: e.target.value })} 
                    placeholder="Tell us about your project needs, goals, and timeline..." 
                    className="w-full bg-black/40 border-2 border-white/20 rounded-2xl px-6 py-4 text-white placeholder-gray-500 h-48 focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all duration-300 outline-none resize-none backdrop-blur-sm"
                  />
                </div>
                <motion.button 
                  type="submit"
                  disabled={submitting} 
                  className="w-full py-5 bg-gradient-to-r from-orange-500 to-orange-600 text-black font-black rounded-2xl hover:from-orange-400 hover:to-orange-500 transition-all duration-500 disabled:opacity-60 flex items-center justify-center gap-4 group shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {submitting ? (
                    <>
                      <div className="w-6 h-6 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      <span>Sending Message...</span>
                    </>
                  ) : (
                    <>
                      <span className="text-lg font-bold">Send Message</span>
                      <ArrowRight className="group-hover:translate-x-2 transition-transform duration-300" size={24} />
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default ProjectDetail
